import { Flex } from 'antd';
import classNames from 'classnames';

import React from 'react';
import { FormattedMessage } from 'react-intl';

import { getContentType } from 'components/common/UploadModal/utils/getContentType';
import { css } from 'config/emotionCss';
import { MEDIA_TYPE } from 'constants/MediaConstants';

import type { AbstractMedia } from 'modules/medias/types/abstract-media';
import type { VideoMedia } from 'modules/medias/types/video-media';
import { DocumentIcon } from 'ui/base/MediaGallery/components/UI';

import VideoPlayer from 'ui/base/VideoPlayer';

import VideoPlayer360 from 'ui/base/VideoPlayer360';

import LazyLoader from 'ui/layout/LazyLoader';

import PdfViewer from '../PdfViewer';
import { isSupportedMediaType } from '../utils';

const imageStyle = css`
  max-width: 100% !important;
  max-height: 75vh !important;
  width: auto;
  height: auto;
  user-select: none;
`;

const unsupportedDocumentStyle = css`
  width: 640px;
  height: 75vh;

  > div {
    background: transparent;
  }
`;

type AvailableMediaDisplayProps = {
  media: AbstractMedia | VideoMedia;
  handleOnClick: () => void;
  autoPlay?: boolean;
};

const Image360 = React.lazy(() => import('ui/base/Image360'));

// Concerning path! -> here as the media is available we must have a path.
const AvailableMediaDisplay: React.FC<AvailableMediaDisplayProps> = ({ media, handleOnClick, autoPlay = false }) => {
  const { type, path, filename } = media;

  // typing of mime.lookup return string or false so we safeguard that.
  const contentType = getContentType(media);
  const extension = filename?.split('.').pop();

  // 360 images and videos should only be displayed in the 360 viewer
  const is360 = type === MEDIA_TYPE.IMAGE_INSIDE_360 || type === MEDIA_TYPE[360];
  const isImage = !is360 && (type === MEDIA_TYPE.IMAGE || contentType?.match?.(/^image\/.*$/));
  const isVideo = !is360 && (type === MEDIA_TYPE.VIDEO || contentType?.match?.(/^video\/.*$/));

  const isPDF = contentType === 'application/pdf';
  const isUnsupportedType = !isSupportedMediaType(media);

  if (isUnsupportedType) {
    return (
      <Flex vertical justify="center" align="center" className={unsupportedDocumentStyle}>
        <DocumentIcon theme="filled" extension={extension} />
        <FormattedMessage id="pdfPreview.error" defaultMessage="This file type is not supported." />
      </Flex>
    );
  }

  return <React.Fragment>
    {isImage && (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
      <img
        src={path}
        className={classNames('d-block mx-auto cursor-pointer', imageStyle)}
        onClick={handleOnClick}
        alt=""
      />
    )}
    {isVideo && (<VideoPlayer bookmarks={(media as VideoMedia).bookmarkList ?? []} url={path!} onEnd={handleOnClick} playing={autoPlay} />)}
    {isPDF && <PdfViewer url={path!} />}
    {type === MEDIA_TYPE[360] && <VideoPlayer360 url={path!} />}
    {type === MEDIA_TYPE.IMAGE_INSIDE_360 && (
      <LazyLoader>
        <Image360 url={path!} />
      </LazyLoader>
    )}
  </React.Fragment>;
};

export default AvailableMediaDisplay;
